<template>
  <div class="tabs__header">
    <v-tabs v-model="tab" class="tabs" @update:model-value="onTabUpdate">
      <template v-if="loading">
        <v-tab v-for="i in 2" :key="i">
          <skeleton-loader width="100px" height="16px" />
        </v-tab>
      </template>

      <v-tab v-for="(tab, index) in tabs" :key="index">
        {{ tab.title }}
      </v-tab>

      <v-spacer />
      <slot v-if="!$vuetify.display.smAndDown" name="append" />
    </v-tabs>
    <div v-if="$vuetify.display.smAndDown && $slots.append" class="mt-4 w-100">
      <slot name="append" />
    </div>
  </div>

  <v-tabs-window v-model="tab" class="tabs__content">
    <v-tabs-window-item v-for="(slot, index) in tabs" :key="index" class="tabs__item">
      <slot :name="`tab.${slot.key}`" />
    </v-tabs-window-item>

    <template v-if="loading">
      <v-tabs-window-item v-for="(_, index) in 2" :key="index" class="tabs__item">
        <slot :name="`tab.${index}`" />
      </v-tabs-window-item>
    </template>
  </v-tabs-window>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { TabsListTab } from '@/core/types/components/components';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';

const $router = useRouter();
const $route = useRoute();

defineProps<{
  tabs: TabsListTab[];
  loading?: boolean;
}>();

const tab = defineModel({ default: 0 });

watch(
  () => $route.query.tab,
  (tabQuery) => {
    if (tabQuery) {
      tab.value = Number(tabQuery);
    }
  },
  {
    immediate: true,
  },
);

const onTabUpdate = (tab: unknown) => {
  $router.push({
    query: {
      tab: tab as number,
    },
  });
};
</script>

<style scoped lang="scss">
.tabs {
  width: 100%;

  &__header {
    display: contents;

    width: 100%;

    & + * {
      padding-top: 24px;
    }

    @include sm-and-down {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }
  }

  &__content {
    flex-grow: 1;

    :deep(.v-window__container) {
      height: 100%;
    }
  }

  &__item {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
  }

  :global(.v-tabs--density-default) {
    --v-tabs-height: 44px;
  }

  :deep(.v-slide-group__container) {
    .v-slide-group__content {
      border-bottom: 1px solid $primary-color;
    }

    .v-tab__slider {
      background-color: transparent !important;
      border: none !important;

      &:before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 50%;

        width: 6px;
        height: 6px;

        background-color: $white-color;
        border: 1px solid $primary-color;
        border-width: 1px 1px 0 0;

        transform: translateX(calc(-50% - 20px)) rotate(-45deg) translateY(50%);
      }
    }

    .v-tab {
      align-items: flex-start;

      padding-top: $gap-2;
      padding-right: 40px;
      padding-left: 0;

      color: $text-secondary-color;

      &:last-of-type:not(:first-of-type) {
        padding-right: 0;
      }

      &.v-tab--selected {
        color: $primary-color;
      }

      .v-ripple__container,
      .v-btn__overlay {
        display: none !important;
      }
    }

    .v-btn__content {
      font-size: $paragraph-s-font-size;
      line-height: $paragraph-s-line-height;
    }
  }
}
</style>
